import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { analytics, readLeadAttribution } from "@/lib/analytics";

// WhatsApp icon component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

export function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const whatsappNumber = "919319266996";
  const whatsappMessage = encodeURIComponent("Hello! I'm interested in learning more about SAILKHA TRADE's products and services.");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const company = formData.get('company') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const interest = formData.get('interest') as string;
    const message = formData.get('message') as string;

    try {
      // Pull attribution stamped by the last quote-CTA click (if any).
      // Defaults to "website_form" so every submission carries a lead_source.
      const attribution = readLeadAttribution();
      const leadSource = attribution?.lead_source || "website_form";
      const leadCategory = attribution?.category;
      const leadLocation = attribution?.location;

      const { data, error } = await supabase.functions.invoke('submit-contact', {
        body: {
          name,
          company: company || undefined,
          email: email || undefined,
          phone,
          interest,
          message,
          lead_source: leadSource,
          lead_category: leadCategory,
          lead_location: leadLocation,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to submit form');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const submissionId: string | undefined = data?.submissionId;

      analytics.contactSubmit(interest, { lead_source: leadSource, category: leadCategory });
      (e.target as HTMLFormElement).reset();
      navigate("/thank-you", {
        state: {
          submissionId,
          name,
          company: company || undefined,
          email: email || undefined,
          phone,
          interest,
          message,
          lead_source: leadSource,
          lead_category: leadCategory,
        },
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      analytics.contactSubmitError(error instanceof Error ? error.message : "unknown");
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-card relative">
      <div className="absolute top-0 left-0 w-full h-px bg-border" />
      
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Info */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-xs tracking-wide-elegant uppercase text-muted-foreground mb-6">
              Get in Touch
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] font-medium">
              Ready to
              <br />
              <span className="text-muted-foreground">Partner?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-12 leading-relaxed">
              Whether you need product sourcing, trading solutions, or want to
              explore a partnership, we're here to help.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-12">
              <a 
                href="mailto:info.sailkha@gmail.com" 
                className="flex items-center gap-4 text-foreground hover:text-muted-foreground transition-colors group"
              >
                <Mail className="w-5 h-5 text-foreground" />
                <span className="hover-underline">info.sailkha@gmail.com</span>
              </a>
              <a 
                href="tel:+919319266996" 
                className="flex items-center gap-4 text-foreground hover:text-muted-foreground transition-colors"
              >
                <Phone className="w-5 h-5 text-foreground" />
                <span className="hover-underline">+91 93192 66996</span>
              </a>
              <div className="flex items-center gap-4 text-muted-foreground">
                <MapPin className="w-5 h-5 text-foreground" />
                <span>India</span>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <motion.a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="inline-flex items-center gap-3 px-6 py-4 bg-[#25D366] text-white font-medium hover:bg-[#20BD5A] transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5" />
              <span>Chat on WhatsApp</span>
              <ArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form
              onSubmit={handleSubmit}
              className="p-8 lg:p-10 border border-border bg-background"
            >
              <h3 className="font-display text-2xl text-foreground mb-8">
                Request a Quote
              </h3>

              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                      Name <span className="text-foreground">*</span>
                    </label>
                    <Input
                      name="name"
                      placeholder="Your name"
                      required
                      className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                      Company
                    </label>
                    <Input
                      name="company"
                      placeholder="Company name"
                      className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                      Phone <span className="text-foreground">*</span>
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                      Email <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
                    </label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                    Interest <span className="text-foreground">*</span>
                  </label>
                  <Input
                    name="interest"
                    placeholder="e.g., Product sourcing, B2B trading, E-commerce..."
                    required
                    className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2 tracking-wide uppercase">
                    Message <span className="text-foreground">*</span>
                  </label>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    required
                    className="rounded-none border-border focus:border-foreground focus:ring-0 bg-transparent resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none h-14 text-sm tracking-wide-elegant uppercase font-medium"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full"
                      />
                      Sending...
                    </span>
                  ) : (
                    <>
                      Send Inquiry
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
